import { useEffect, useState } from "react";

import { usePropertyQuery } from "api/property/usePropertyQuery";
import type { Media } from "api/schemas";
import { useOptionalExternalServicesState } from "features/ExternalServices/context";
import { useLocation } from "shared/Router";
import { themeVar } from "theme/themeVars";
import { constructImageUrl } from "utils/constructImageUrl";

type Props = {
  propertySlug: string | undefined;
};

const propertyNameClassName = "block max-w-full truncate font-black text-white";

const propertyNameStyle = {
  fontFamily: "var(--font-heading)",
  fontSize: "clamp(1.5rem, 5vw, 2rem)",
  lineHeight: 1.1,
  margin: 0,
} as const;

/** Reserved height for image logos only — prevents CLS while the file loads. */
const imageLogoSlotClassName =
  "flex h-8 min-h-8 max-h-8 w-full max-w-full items-center sm:h-10 sm:min-h-10 sm:max-h-10 sm:max-w-[200px] md:max-w-[240px]";

const Header = ({ propertySlug }: Props) => {
  const propertyQuery = usePropertyQuery(propertySlug, {
    enabled: !!propertySlug,
  });
  const data = propertyQuery?.data?.data as
    | (NonNullable<typeof propertyQuery>["data"]["data"] & {
        logo?: Media | null;
      })
    | undefined;
  const isPropertyPending = propertyQuery.isLoading && !data;
  const address = data
    ? `${data?.address_street}, ${data.address_city}, ${data.address_state} ${data.address_zip}, ${data.address_country}`
    : "";

  const logoUrl = data?.logo
    ? constructImageUrl(data.logo, { width: 320 })
    : null;
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  const { pathname } = useLocation();
  const isResultsPage =
    pathname.includes("/results") && !pathname.includes("/favorites");

  const externalServicesState = useOptionalExternalServicesState();
  const scheduleTourExternalUrl =
    externalServicesState?.scheduleTourExternalUrl;

  const scheduleTourButtonClassName =
    "px-4! text-[14px]! font-[500] py-[10px]! rounded-lg! shrink-0 whitespace-nowrap sm:absolute sm:right-4 sm:top-1/2 sm:-translate-y-1/2";
  const scheduleTourButtonStyle = {
    backgroundColor: themeVar.colorPrimaryBtnBg ?? "#FF8777",
    color: themeVar.colorPrimaryBtnText ?? "#0C0A09",
  };

  const handleScheduleTourScroll = () => {
    const el = document.getElementById("schedule-form");
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const showScheduleButton = isResultsPage;

  const mobileBrandWrapperClass = showScheduleButton ? "flex-1" : "flex-none";
  const mobileGridAlignClass = showScheduleButton
    ? "justify-items-start sm:justify-items-center"
    : "justify-items-center";
  const mobileTextAlignClass = showScheduleButton
    ? "text-left sm:text-center"
    : "text-center";
  const mobileLogoObjectClass = showScheduleButton
    ? "object-left sm:object-center"
    : "object-center";
  const mobileHeaderJustifyClass = showScheduleButton
    ? "justify-between"
    : "justify-center";

  const handleScheduleTourExternalClick = () => {
    externalServicesState?.onScheduleTourUrlClick?.();
  };

  const renderPropertyName = () => (
    <span
      role="heading"
      aria-level={1}
      className={`${propertyNameClassName} ${mobileTextAlignClass}`}
      style={propertyNameStyle}
    >
      {data?.name}
    </span>
  );

  const renderBrandTitle = () => {
    const imageLogoJustifyClass = showScheduleButton
      ? "justify-start sm:justify-center"
      : "justify-center";

    if (logoUrl && !logoFailed) {
      return (
        <div className={`${imageLogoSlotClassName} ${imageLogoJustifyClass}`}>
          <img
            src={logoUrl}
            alt={data?.name ?? "Property logo"}
            width={200}
            height={40}
            decoding="async"
            onError={() => setLogoFailed(true)}
            className={`max-h-full max-w-full object-contain ${mobileLogoObjectClass}`}
          />
        </div>
      );
    }

    if (isPropertyPending) {
      return null;
    }

    return renderPropertyName();
  };

  const renderAddress = () => (
    <a
      rel="noreferrer noopener"
      href={`https://maps.google.com/?q=${address}`}
      target="_blank"
      className={`block max-w-full truncate text-[10px] underline sm:text-[14px] ${mobileTextAlignClass}`}
      style={{ lineHeight: 1.2, margin: 0, color: "#fff" }}
    >
      {address}
    </a>
  );

  const renderScheduleTourButton = () => {
    if (!isResultsPage) return null;

    if (scheduleTourExternalUrl) {
      return (
        <a
          href={scheduleTourExternalUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Schedule a tour (opens in a new tab)"
          onClick={handleScheduleTourExternalClick}
          className={scheduleTourButtonClassName}
          style={scheduleTourButtonStyle}
        >
          Schedule Tour
        </a>
      );
    }

    return (
      <button
        type="button"
        onClick={handleScheduleTourScroll}
        className={scheduleTourButtonClassName}
        style={scheduleTourButtonStyle}
      >
        Schedule Tour
      </button>
    );
  };

  return (
    <div
      className={`sticky top-0 z-11 flex min-h-[70px] w-full items-center gap-3 overflow-visible px-4 py-3 sm:min-h-[83px] sm:justify-center sm:px-4 sm:pt-4 sm:pb-[15px] ${mobileHeaderJustifyClass}`}
      style={{ backgroundColor: themeVar.colorBannerBg }}
    >
      <div
        className={`flex min-w-0 sm:absolute sm:left-1/2 sm:top-1/2 sm:w-auto sm:max-w-none sm:flex-none sm:-translate-x-1/2 sm:-translate-y-1/2 sm:items-center sm:justify-center ${mobileBrandWrapperClass}`}
      >
        <div
          className={`grid max-w-full grid-rows-[auto_auto] gap-1 leading-none ${mobileGridAlignClass}`}
        >
          {renderBrandTitle()}
          {renderAddress()}
        </div>
      </div>

      {showScheduleButton ? (
        <div className="shrink-0 self-center sm:ml-auto">
          {renderScheduleTourButton()}
        </div>
      ) : null}
    </div>
  );
};
export default Header;
